import React, { useCallback, lazy, useState, Suspense, useEffect } from 'react';
import { Space, Dropdown, Menu, Collapse } from 'antd';
import {
  isEmpty as _isEmpty,
  map as _map,
  take as _take,
  forEach as _forEach,
  delay as _delay,
  last as _last,
  isUndefined as _isUndefined,
  uniqBy as _uniqBy
} from 'lodash-es';
import DOMPurify from 'dompurify';
import Markdown from 'markdown-to-jsx';
import he from 'he';
import Icon from 'components/Icon/Icon';
import { staticQuestionsToAvoid } from '../utility/utility';
import GraasTable from 'components/Atoms/GraasTable/GraasTable';
import TooltipWrapper from 'components/Atoms/TooltipWrapper/TooltipWrapper';
import { useNavigate } from 'react-router-dom';
import { stringDateToUnixTimestamp } from 'utilities/utilities_exports';
import GraasHopperChart from './GraasHopperChart';

const ConnectJournalDrawer = lazy(() =>
  import(
    /* webpackChunkName: 'ConnectJournalDrawer' */ 'Modules/Connect/components/ConnectJournalDrawer/ConnectJournalDrawer'
  )
);

const HopperConversation = props => {
  const {
    userInitial = 'A',
    chatConversation,
    isQuestionLoading,
    isLoadingResponse,
    onSendMessage,
    actionRequiredChannel,
    isOnboardingActive,
    chatConvIndex,
    openHopprDrawer,
    dashboardMap
  } = props;

  const navigate = useNavigate();
  const { Panel } = Collapse;
  const [openDrawer, setOpenDrawer] = useState(false);
  const [lastUpdate, setLastUpdate] = useState('');

  useEffect(() => {
    let lastUpdate = _last(chatConversation?.loadingResponses)?.update || '';
    if (!_isEmpty(lastUpdate)) {
      setLastUpdate(lastUpdate);
    }
  }, [chatConversation?.loadingResponses]);

  // * Construct table if table data available
  const constructTable = useCallback(tableData => {
    const { columns, data_source, is_empty } = tableData;

    if (!is_empty) {
      return (
        <GraasTable className='c-customTable customSmallTable' data={tableData} showSummary={false} chartHeight={500} />
      );
    }
  });

  const constructChart = useCallback(chartData => {
    const { is_empty } = chartData;
    if (!is_empty) {
      return <GraasHopperChart data={chartData} />;
    }
  });

  const handledRedirect = redirectUrl => {
    openHopprDrawer(false);
    navigate(redirectUrl + `&t=${Date.now()}`);
  };

  const constructAnswer = responseObject => {
    if (responseObject?.isOnboardingQuestion && !_isUndefined(responseObject?.chart_name)) {
      setTimeout(() => {
        const redirectButton = document?.getElementById(
          responseObject?.conversation_id === '3' ? 'verify_hoppr_q1' : 'verify_hoppr_q2'
        );
        let redirectUrl = he.decode(redirectButton?.getAttribute('data-value'));
        const queryString = redirectUrl.split('?')[1];
        const queryParams = Object.fromEntries(new URLSearchParams(queryString));
        if (queryParams?.tab && dashboardMap?.[queryParams?.tab]) {
          redirectUrl += `&id=${dashboardMap?.[queryParams?.tab]}`;
        }
        if (redirectButton) {
          redirectButton.addEventListener('click', () => handledRedirect(redirectUrl));
        }
      }, 100);
    }
    return responseObject?.answer;
  };

  const renderLoadingThoughts = chatConversation => {
    const getThoughts = () => {
      return _map(
        _uniqBy(chatConversation?.loadingResponses, 'thought').sort((a, b) => a.timeInserted - b.timeInserted),
        (item, index) => {
          if (!_isEmpty(item?.thought)) {
            return {
              label: (
                <div className='customThought'>
                  <span className='customSelect_menu no-icons c-heading sm mb-0 dont-break'>{item.thought}</span>
                </div>
              ),
              key: index
            };
          }
          return null;
        }
      )
        .filter(Boolean)
        .map(item => <p key={item.key}>{item?.label}</p>);
    };

    const getUpdateMenuItems = () => {
      return _map(_uniqBy(chatConversation?.loadingResponses, 'update'), (item, index) => {
        if (!_isEmpty(item?.update)) {
          return {
            label: <span className='customSelect_menu no-icons c-heading sm mb-0 dont-break'>{item.update}</span>,
            key: index
          };
        }
        return null;
      }).filter(Boolean);
    };

    const thoughts = getThoughts();

    if (_isEmpty(thoughts)) {
      return (
        <div className='chatAnswer eventList'>
          <span className='content'>
            <Dropdown
              overlay={
                <div className='customSelect_menuWrapper'>
                  <Menu items={getUpdateMenuItems()} />
                </div>
              }
              placement='bottomRight'
              trigger={['hover']}
              className='c-settingDropdown'
              overlayClassName='customSelect_overlay hoppr'
            >
              <button
                onClick={e => e.preventDefault()}
                style={{
                  fontWeight: 'normal',
                  color: '#3b404d',
                  fontSize: '14px',
                  backgroundColor: 'transparent'
                }}
              >
                <Space>
                  {_last(chatConversation?.loadingResponses)?.update || 'The thoughts that went into getting this data'}
                  <Icon name='keyboard_arrow_down' isBordered />
                  {chatConversation?.isLoading && (
                    <div className='dots-loading dark'>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  )}
                </Space>
              </button>
            </Dropdown>
          </span>
        </div>
      );
    }

    return (
      <div className='chatAnswer thoughtList'>
        <Collapse
          className='Graas-collapse right-icon is-clean'
          defaultActiveKey={['key-1']}
          expandIcon={({ isActive }) => (
            <Icon name={isActive ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} style={{ fontSize: '24px' }} />
          )}
        >
          <Panel
            header={
              <span className='Graas-heading size-5 bold mgb-0'>{`Thought for ${calculateProcessingTime(
                chatConversation
              )} sec`}</span>
            }
            key='1'
            className='site-collapse-custom-panel'
          >
            <div className='custom-thoughts-container'>{thoughts}</div>
          </Panel>
        </Collapse>
      </div>
    );
  };

  const getMaxResponseTime = responses => {
    if (!responses || responses.length === 0) return 0;
    return responses.reduce((maxTime, { response_time }) => Math.max(maxTime, response_time), 0);
  };

  const calculateProcessingTime = chatConversation => {
    const requestTimestamp = stringDateToUnixTimestamp(chatConversation?.request_time) / 1000;

    const answerFinishedTimestamp = Math.max(
      getMaxResponseTime(chatConversation?.responses),
      getMaxResponseTime(chatConversation?.loadingResponses?.responses)
    );
    return answerFinishedTimestamp - requestTimestamp;
  };

  return (
    <>
      {!staticQuestionsToAvoid.includes(chatConversation?.question_text) &&
        !_isEmpty(chatConversation?.question_text) && (
          <div className='chatAnswer byUser' key={chatConversation?.conversation_id} data-tooltip={userInitial}>
            <span className='content'>{chatConversation?.question_text}</span>
          </div>
        )}

      {/* {chatConversation?.conversation_type === 'request' &&
        _isEmpty(chatConversation?.responses) &&
        chatConversation?.conversation_id === 2 && <SMResult type='loader' message='Loading Response' size='sm' />} */}

      {/* //!  add all loading steps options inside dropdown */}
      {!staticQuestionsToAvoid.includes(chatConversation?.question_text) &&
        _isUndefined(chatConversation?.isOnboardingQuestion) && (
          <div className={`loadingConversationContainer ${chatConversation?.isLoading ? 'loading' : 'loaded'}`}>
            <>
              <div className={`chatAnswer `} key={1}>
                <span className='content loading'>
                  <div>
                    {lastUpdate}
                    {_isEmpty(chatConversation?.responses) && (
                      <div className='dots-loading'>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    )}
                  </div>
                </span>
              </div>
            </>
          </div>
        )}

      {!chatConversation?.isLoading && !_isEmpty(chatConversation?.responses) && (
        <>
          {/* //!  All loading options inside dropdown once final answer is ready */}
          {!_isEmpty(chatConversation?.loadingResponses) &&
            !staticQuestionsToAvoid.includes(chatConversation?.question_text) &&
            renderLoadingThoughts(chatConversation)}

          {_map(chatConversation?.responses, (responseObject, index) => {
            return (
              <React.Fragment key={index}>
                {!_isEmpty(responseObject?.answer) && responseObject?.status !== 'error' && (
                  <>
                    <div className='chatAnswer' key={index}>
                      <span className='content'>
                        {<Markdown>{DOMPurify.sanitize(constructAnswer(responseObject))}</Markdown>}
                      </span>
                    </div>
                  </>
                )}

                {/* //? Error Message handling */}
                {responseObject?.status === 'error' && (
                  <div className='chatAnswer' key={index}>
                    <span className='content'>
                      <Icon name='warning' color='#d92d20' className='mr-1' />
                      An unexpected error occurred while getting data.
                      <TooltipWrapper
                        title={
                          <span className='c-heading sm dont-break color-white mb-0'>
                            {<Markdown>{DOMPurify.sanitize(responseObject?.answer)}</Markdown>}
                          </span>
                        }
                        isShowTooltip={true}
                        overlayClassName={'graas_custom_toolTip'}
                      >
                        <button className='settingButton sm'>
                          <Icon name='info' size='small' />
                        </button>
                      </TooltipWrapper>
                    </span>
                  </div>
                )}

                {responseObject?.chart_type === 'table' &&
                  !_isEmpty(responseObject?.chart_data) &&
                  constructTable(responseObject?.chart_data)}
                {responseObject?.chart_type !== 'table' &&
                  !_isEmpty(responseObject?.chart_data) &&
                  constructChart(responseObject?.chart_data)}

                {!_isEmpty(responseObject?.suggested_questions) &&
                  (!isOnboardingActive || chatConversation?.isOnboardingQuestion || chatConvIndex > 6) && (
                    <div className='suggestedQuestionContainer'>
                      {_map(responseObject?.suggested_questions, question => {
                        return (
                          <div className='chatAnswer question' key={question}>
                            <button
                              className='c-button is-bordered is-round text-left'
                              onClick={() => {
                                // ! commented this because Ajinkya want to initiate conversation on click of question.
                                //! instead of clicking on ask button.
                                // setSearchPayload(question);
                                _delay(() => {
                                  onSendMessage(question);
                                }, 200);
                              }}
                              disabled={isLoadingResponse || isQuestionLoading}
                            >
                              {question}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
              </React.Fragment>
            );
          })}

          {isQuestionLoading &&
            !_isEmpty(chatConversation?.currentResponse) &&
            !_isEmpty(chatConversation?.currentResponse?.update) && (
              <div className={`chatAnswer `}>
                <span className='content loading'>
                  <Space>
                    {chatConversation?.currentResponse?.update}
                    <div className='dots-loading'>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </Space>
                </span>
              </div>
            )}
        </>
      )}
      {openDrawer && (
        <Suspense fallback={<></>}>
          <ConnectJournalDrawer
            openDrawer={openDrawer}
            setOpenDrawer={setOpenDrawer}
            actionRequiredChannel={actionRequiredChannel}
          />
        </Suspense>
      )}
    </>
  );
};

export default HopperConversation;
